import HttpRequest from '../lib/HttpRequest';
import FormNavigator from '../lib/FormNavigator';
import MessageModal from './MessageModal';

export default class PasswordSettingModal {
  private modal: HTMLElement | null = null;
  private backdrop: HTMLElement | null = null;
  private newPasswordInput: HTMLInputElement | null = null;
  private newPasswordReEnterInput: HTMLInputElement | null = null;
  private updateButton: HTMLButtonElement | null = null;
  private cancelButton: HTMLButtonElement | null = null;
  private isInitialized = false;

  private messageModal = new MessageModal();

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    // モーダルのHTML構造を動的に生成
    this.createModal();
    this.isInitialized = true;
  }

  private createModal(): void {
    // 確認モーダルのバックドロップ
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'modal-backdrop fade';
    this.backdrop.style.display = 'none';

    // 確認モーダル本体
    this.modal = document.createElement('div');
    this.modal.className = 'modal fade';
    this.modal.setAttribute('tabindex', '-1');
    this.modal.style.display = 'none';

    this.modal.innerHTML = `
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">パスワード設定</h5>
          </div>
          <div class="modal-body">
            <form>
              <div class="mb-3">
                <label for="newPassword" class="form-label">新しいパスワード</label>
                <input type="password" class="form-control" id="newPassword" maxlength="64" placeholder="新しいパスワード" />
              </div>
              <div class="mb-3">
                <label for="newPasswordReEnter" class="form-label">新しいパスワード再入力</label>
                <input type="password" class="form-control" id="newPasswordReEnter" maxlength="64" placeholder="新しいパスワード再入力" />
              </div>
            </form>
          </div>
          <div class="modal-footer justify-content-start">
            <button type="button" class="btn btn-secondary btn-cancel">キャンセル</button>
            <button type="button" class="btn btn-primary btn-update">更新</button>
          </div>
        </div>
      </div>
    `;

    // 要素の参照を保存
    this.newPasswordInput = this.modal.querySelector('#newPassword');
    this.newPasswordReEnterInput = this.modal.querySelector('#newPasswordReEnter');
    this.updateButton = this.modal.querySelector('.btn-update');
    this.cancelButton = this.modal.querySelector('.btn-cancel');

    // イベントリスナーの設定
    this.setupEventListeners();

    // DOMに追加
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.modal);

    // CSSアニメーションを追加
    this.addAnimationStyles();
  }

  /**
   * イベントリスナーの設定
   */
  private setupEventListeners(): void {
    // 「更新」ボタンのクリックイベント
    this.updateButton?.addEventListener('click', async () => {
      const button = this.updateButton;
      if (button) {
        button.style.transform = 'scale(0.95)';
        setTimeout(async () => {
          button.style.transform = 'scale(1)';
          await this.handleUpdate();
        }, 150);
      }
    });

    // 「キャンセル」ボタンのクリックイベント
    this.cancelButton?.addEventListener('click', () => {
      this.hide();
    });
  }

  /**
   * アニメーション用のスタイルを追加
   */
  private addAnimationStyles(): void {
    // 既存のスタイルタグがあるか確認
    if (document.getElementById('password-setting-modal-style')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'password-setting-modal-style';
    style.textContent = `
      .modal.show .modal-dialog {
        transform: translate(0, 0);
        transition: transform 0.3s ease-out;
      }

      .modal.fade .modal-dialog {
        transform: translate(0, -50px);
        transition: transform 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }

  public show(): void {
    if (!this.isInitialized || !this.modal || !this.backdrop) {
      return;
    }

    // モーダルを表示
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';

    // アニメーションクラスを追加
    setTimeout(() => {
      this.backdrop?.classList.add('show');
      this.modal?.classList.add('show');
    }, 10);
  }

  /**
   * モーダルを閉じる
   */
  public hide(onClose?: () => void): void {
    if (!this.modal || !this.backdrop) return;

    // アニメーションクラスを削除
    this.modal.classList.remove('show');
    this.backdrop.classList.remove('show');

    // 完全に非表示にする
    setTimeout(() => {
      if (this.modal) this.modal.style.display = 'none';
      if (this.backdrop) this.backdrop.style.display = 'none';

      // コールバックを実行
      if (onClose) {
        onClose();
      }
    }, 300);
  }

  /**
   * 更新ボタン押下時の処理
   */
  private async handleUpdate(): Promise<void> {
    if (!this.validateInput()) {
      return;
    }

    const password = this.newPasswordInput?.value;

    try {
      // サーバーへリクエスト送信
      const response = await HttpRequest.post('/api/login/password/register', {
        rawPassword: password
      });

      if (response.status == 200) {
        this.hide(() => {
          this.messageModal.showInfo("パスワードの設定が完了しました。", "", () => {
            // ログイン画面へ遷移
            FormNavigator.getNavigate('/login');
          });
        });
      } else {
        this.hide(() => {
          this.messageModal.showError("通信に失敗しました。" + response.status);
        });
      }
    } catch (error: any) {
      this.hide(() => {
        this.messageModal.showError("予期せぬエラーが発生しました。", "", () => {
          FormNavigator.getNavigate('/error');
        });
      });
    }
  }

  private validateInput(): boolean {
    const newPassword = this.newPasswordInput?.value;
    if (newPassword == null || newPassword === '') {
      this.hide(() => {
        this.messageModal.showWarning("新しいパスワードを入力してください。", "", () => {
          this.show();
        });
      });
      return false;
    }

    const newPasswordReEnter = this.newPasswordReEnterInput?.value;
    if (newPasswordReEnter == null || newPasswordReEnter === '') {
      this.hide(() => {
        this.messageModal.showWarning("新しいパスワード再入力を入力してください。", "", () => {
          this.show();
        });
      });
      return false;
    }

    if (newPassword != newPasswordReEnter) {
      this.hide(() => {
        this.messageModal.showWarning("新しいパスワードと新しいパスワード再入力が一致しません。", "", () => {
          this.show();
        });
      });
      return false;
    }

    return true;
  }

}
